import { Link } from 'react-router-dom'
import { usePendingChanges, useTriggerDeploy } from '@/hooks'

// Only show sync button in production (not in development)
const SHOW_SYNC_BUTTON = !import.meta.env.DEV

interface ClerkUser {
  id: string
  email: string | undefined
  fullName: string | null
  imageUrl: string
}

interface HeaderProps {
  user: ClerkUser | null
  onLogout: () => Promise<void>
}

/**
 * Componente de encabezado con marca A1Homes e información de usuario.
 * Muestra el logo, perfil de usuario, botón de sincronización y botón de cerrar sesión.
 * La navegación es manejada por el componente Sidebar (escritorio y móvil).
 */
export function Header({ user, onLogout }: HeaderProps) {
  const { count: pendingCount } = usePendingChanges()
  const { trigger: triggerDeploy, isPending: isDeploying } = useTriggerDeploy()

  async function handleLogout() {
    await onLogout()
  }

  async function handleSync() {
    try {
      await triggerDeploy()
    } catch (error) {
      console.error('Failed to trigger deploy:', error)
    }
  }

  return (
    <header className="sticky top-0 z-50 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-16 items-center justify-between px-4 md:px-6">
        {/* Logo and Brand */}
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-md bg-primary">
            <span className="text-sm font-bold text-primary-foreground">A1</span>
          </div>
          <span className="text-xl font-bold tracking-tight">
            A1Homes <span className="text-muted-foreground font-normal">Admin</span>
          </span>
        </Link>

        {/* Información de Usuario y Acciones */}
        <div className="flex items-center gap-4">
          {/* Sync Button - Only shown in production */}
          {SHOW_SYNC_BUTTON && (
            <button
              onClick={handleSync}
              disabled={isDeploying || pendingCount === 0}
              className="relative inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
              title={pendingCount > 0 ? `${pendingCount} cambios pendientes` : 'Sin cambios pendientes'}
            >
              {isDeploying ? (
                <LoaderIcon className="h-4 w-4 animate-spin" />
              ) : (
                <CloudUploadIcon className="h-4 w-4" />
              )}
              <span className="hidden sm:inline">
                {isDeploying ? 'Publicando...' : 'Publicar'}
              </span>
              {pendingCount > 0 && !isDeploying && (
                <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-[10px] font-bold text-white">
                  {pendingCount > 9 ? '9+' : pendingCount}
                </span>
              )}
            </button>
          )}

          <div className="flex items-center gap-3">
            {/* Avatar de Usuario */}
            {user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt={user.fullName || user.email || 'Usuario'}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-sm font-medium">
                {user?.email?.charAt(0).toUpperCase() || user?.fullName?.charAt(0).toUpperCase() || 'A'}
              </div>
            )}
            {/* Email o Nombre de Usuario - Oculto en móvil */}
            <span className="hidden text-sm text-muted-foreground md:inline">
              {user?.fullName || user?.email || 'Usuario Admin'}
            </span>
          </div>

          {/* Botón de Cerrar Sesión */}
          <button
            onClick={handleLogout}
            className="inline-flex items-center justify-center rounded-md px-3 py-1.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <LogoutIcon className="mr-1.5 h-4 w-4" />
            <span className="hidden sm:inline">Salir</span>
          </button>
        </div>
      </div>
    </header>
  )
}

/**
 * Componente de icono de cerrar sesión
 */
function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </svg>
  )
}

/**
 * Componente de icono de subir a la nube
 */
function CloudUploadIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" />
      <path d="M12 12v9" />
      <path d="m16 16-4-4-4 4" />
    </svg>
  )
}

/**
 * Componente de icono de cargando
 */
function LoaderIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

export default Header
